import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Button, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip,
  Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, FormControlLabel,
  Switch, Checkbox, FormGroup, Alert,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { api } from '../../config/api';

const CANALES = [
  { id: 'whatsapp', label: 'WhatsApp API' }, { id: 'qr', label: 'WhatsApp QR' },
  { id: 'instagram', label: 'Instagram' }, { id: 'messenger', label: 'Messenger' }, { id: 'widget', label: 'Widget web' },
];
const clp = (n: any) => `$${Math.round(Number(n || 0)).toLocaleString('es-CL')}`;
const VACIO = {
  codigo: '', nombre: '', descripcion: '', precio_clp: 29990, periodo_dias: 30, trial_dias: 7,
  limite_mensajes_mes: '', limite_tokens_mes: '', limite_agentes: 3, precio_mensaje_extra_clp: 0,
  canales: CANALES.map((c) => c.id), features: [] as string[], publico: true, activo: true, orden: 0,
};

const AdminPlanes: React.FC = () => {
  const [planes, setPlanes] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [error, setError] = useState('');

  const cargar = () => api.get('/admin/planes').then((r) => setPlanes(r.data.planes || []));
  useEffect(() => { cargar(); }, []);

  const abrir = (p?: any) => {
    setError('');
    setEdit(p ? {
      ...p, limite_mensajes_mes: p.limite_mensajes_mes ?? '', limite_tokens_mes: p.limite_tokens_mes ?? '',
      featuresTxt: (p.features || []).join('\n'),
    } : { ...VACIO, featuresTxt: '' });
  };

  const guardar = async () => {
    setError('');
    const body = { ...edit, features: String(edit.featuresTxt || '').split('\n').map((s: string) => s.trim()).filter(Boolean) };
    try {
      if (edit.id) await api.put(`/admin/planes/${edit.id}`, body); else await api.post('/admin/planes', body);
      setEdit(null); cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo guardar'); }
  };

  const set = (k: string) => (e: any) => setEdit({ ...edit, [k]: e.target.value });
  const toggleCanal = (c: string) => setEdit({ ...edit, canales: edit.canales.includes(c) ? edit.canales.filter((x: string) => x !== c) : [...edit.canales, c] });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Los cambios de precio aplican a nuevas contrataciones y renovaciones. Los periodos ya pagados no cambian.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => abrir()}>Nuevo plan</Button>
      </Stack>
      <Card><TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Plan</TableCell><TableCell align="right">Precio</TableCell><TableCell>Periodo / prueba</TableCell>
            <TableCell>Límites</TableCell><TableCell>Canales</TableCell><TableCell>Suscriptores</TableCell><TableCell>Estado</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            {planes.map((p) => (
              <TableRow key={p.codigo} hover>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nombre}</Typography><Typography variant="caption" color="text.secondary">{p.codigo}</Typography></TableCell>
                <TableCell align="right">{clp(p.precio_clp)}</TableCell>
                <TableCell>{p.periodo_dias} días / {p.trial_dias} de prueba</TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">{p.limite_mensajes_mes ? `${Number(p.limite_mensajes_mes).toLocaleString('es-CL')} resp/mes` : 'Respuestas ilimitadas'}</Typography>
                  <Typography variant="caption" display="block">{p.limite_tokens_mes ? `${Number(p.limite_tokens_mes).toLocaleString('es-CL')} tokens/mes` : 'Tokens ilimitados'}</Typography>
                  <Typography variant="caption" display="block">{p.limite_agentes || 1} agente{(p.limite_agentes || 1) === 1 ? '' : 's'}</Typography>
                  {p.precio_mensaje_extra_clp > 0 && <Typography variant="caption" display="block">Extra: {clp(p.precio_mensaje_extra_clp)} c/u</Typography>}
                </TableCell>
                <TableCell><Typography variant="caption">{(p.canales || []).join(', ')}</Typography></TableCell>
                <TableCell>{p.suscriptores ?? 0}</TableCell>
                <TableCell>
                  <Chip size="small" color={p.activo ? 'success' : 'default'} label={p.activo ? 'Activo' : 'Inactivo'} />
                  {!p.publico && <Chip size="small" sx={{ ml: 0.5 }} label="Oculto" />}
                </TableCell>
                <TableCell>{p.id && <IconButton size="small" onClick={() => abrir(p)}><Edit fontSize="small" /></IconButton>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer></Card>

      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edit?.id ? `Editar ${edit.nombre}` : 'Nuevo plan'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {edit && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}><TextField fullWidth size="small" label="Código" value={edit.codigo} onChange={set('codigo')} disabled={!!edit.id} helperText="Ej: basico, pro, empresa" /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Nombre" value={edit.nombre} onChange={set('nombre')} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Descripción" value={edit.descripcion || ''} onChange={set('descripcion')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Precio CLP" value={edit.precio_clp} onChange={set('precio_clp')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Periodo (días)" value={edit.periodo_dias} onChange={set('periodo_dias')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Días de prueba" value={edit.trial_dias} onChange={set('trial_dias')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Respuestas/mes" value={edit.limite_mensajes_mes} onChange={set('limite_mensajes_mes')} helperText="Vacío = ilimitado" /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Tokens IA/mes" value={edit.limite_tokens_mes} onChange={set('limite_tokens_mes')} helperText="Vacío = ilimitado" /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Precio resp. extra" value={edit.precio_mensaje_extra_clp} onChange={set('precio_mensaje_extra_clp')} helperText="0 = se detiene" /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Agentes" value={edit.limite_agentes ?? 1} onChange={set('limite_agentes')} inputProps={{ min: 1, max: 20 }} helperText="Cada uno con su número. En la prueba gratis es 1." /></Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Canales incluidos</Typography>
                <FormGroup row>
                  {CANALES.map((c) => <FormControlLabel key={c.id} control={<Checkbox size="small" checked={edit.canales.includes(c.id)} onChange={() => toggleCanal(c.id)} />} label={c.label} />)}
                </FormGroup>
              </Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={3} size="small" label="Características (una por línea)" value={edit.featuresTxt} onChange={set('featuresTxt')} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Orden" value={edit.orden} onChange={set('orden')} /></Grid>
              <Grid item xs={8}>
                <FormControlLabel control={<Switch checked={!!edit.activo} onChange={(e) => setEdit({ ...edit, activo: e.target.checked })} />} label="Activo" />
                <FormControlLabel control={<Switch checked={!!edit.publico} onChange={(e) => setEdit({ ...edit, publico: e.target.checked })} />} label="Visible para contratar" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Cancelar</Button><Button variant="contained" onClick={guardar}>Guardar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPlanes;
